import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  storefrontApiRequest,
  CART_QUERY,
  CART_CREATE_MUTATION,
  CART_LINES_ADD_MUTATION,
  CART_LINES_UPDATE_MUTATION,
  CART_LINES_REMOVE_MUTATION,
  formatCheckoutUrl,
  isCartNotFoundError,
  type ShopifyProduct,
} from '@/lib/shopify/api';
import { trackAddToCart, trackInitiateCheckout } from '@/lib/analytics';

export interface ShopifyCartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

interface ShopifyCartStore {
  items: ShopifyCartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  isDrawerOpen: boolean;
  lastAddedAt: number;
  setDrawerOpen: (open: boolean) => void;
  addItem: (item: Omit<ShopifyCartItem, 'lineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

export const useShopifyCartStore = create<ShopifyCartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      isDrawerOpen: false,
      lastAddedAt: 0,
      setDrawerOpen: (open: boolean) => set({ isDrawerOpen: open }),

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);

        set({ isLoading: true });
        try {
          if (!cartId) {
            const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
              input: { lines: [{ quantity: item.quantity, merchandiseId: item.variantId }] },
            });
            if (data?.data?.cartCreate?.userErrors?.length > 0) {
              console.error('Cart creation failed:', data.data.cartCreate.userErrors);
              return;
            }
            const cart = data?.data?.cartCreate?.cart;
            if (!cart?.checkoutUrl) return;
            const lineId = cart.lines.edges[0]?.node?.id;
            if (!lineId) return;
            set({
              cartId: cart.id,
              checkoutUrl: formatCheckoutUrl(cart.checkoutUrl),
              items: [{ ...item, lineId }],
              isDrawerOpen: true,
              lastAddedAt: Date.now(),
            });
          } else if (existingItem) {
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) return;
            const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
              cartId,
              lines: [{ id: existingItem.lineId, quantity: newQuantity }],
            });
            const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
            if (isCartNotFoundError(userErrors)) { clearCart(); return; }
            if (userErrors.length > 0) return;
            const currentItems = get().items;
            set({ items: currentItems.map(i => i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i), isDrawerOpen: true, lastAddedAt: Date.now() });
          } else {
            const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
              cartId,
              lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
            });
            const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
            if (isCartNotFoundError(userErrors)) { clearCart(); return; }
            if (userErrors.length > 0) return;
            const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
            const newLine = lines.find((l: { node: { id: string; merchandise: { id: string } } }) => l.node.merchandise.id === item.variantId);
            const currentItems = get().items;
            set({ items: [...currentItems, { ...item, lineId: newLine?.node?.id ?? null }], isDrawerOpen: true, lastAddedAt: Date.now() });
          }

          trackAddToCart({ product: item.product.node, quantity: item.quantity });
        } catch (error) {
          console.error('Failed to add item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) { await get().removeItem(variantId); return; }
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
            cartId,
            lines: [{ id: item.lineId, quantity }],
          });
          const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
          if (isCartNotFoundError(userErrors)) { clearCart(); return; }
          if (userErrors.length > 0) return;
          const currentItems = get().items;
          set({ items: currentItems.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
        } catch (error) {
          console.error('Failed to update quantity:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
            cartId,
            lineIds: [item.lineId],
          });
          const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
          if (isCartNotFoundError(userErrors)) { clearCart(); return; }
          if (userErrors.length > 0) return;
          const currentItems = get().items;
          const newItems = currentItems.filter(i => i.variantId !== variantId);
          newItems.length === 0 ? clearCart() : set({ items: newItems });
        } catch (error) {
          console.error('Failed to remove item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),
      getCheckoutUrl: () => {
        const checkoutUrl = get().checkoutUrl;
        return checkoutUrl ? formatCheckoutUrl(checkoutUrl) : null;
      },

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) {
            clearCart();
            return;
          }

          if (cart.checkoutUrl) {
            const normalizedCheckoutUrl = formatCheckoutUrl(cart.checkoutUrl);
            if (normalizedCheckoutUrl !== get().checkoutUrl) {
              set({ checkoutUrl: normalizedCheckoutUrl });
            }
          }
        } catch (error) {
          console.error('Failed to sync cart:', error);
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl }),
      merge: (persistedState, currentState) => {
        const mergedState = {
          ...currentState,
          ...(persistedState as Partial<ShopifyCartStore>),
        };

        return {
          ...mergedState,
          checkoutUrl: mergedState.checkoutUrl ? formatCheckoutUrl(mergedState.checkoutUrl) : null,
        };
      },
    }
  )
);
